import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Divider from '@mui/material/Divider';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import apiService from '../services/api';
import { useAuth } from '../auth/AuthContext';
import { BankAccount, DepositType, BankAccountResponse } from '../types';
import { scrollToFirstError } from '../utils/formValidation';
import { convertToHankaku, findInvalidChars } from '../utils/kanaConvert';
import { parseSokyuBankAccountError } from '../utils/sokyuErrorParser';
import TermsConsentPopup from '../components/TermsConsentPopup';
import { SOKYU_TERMS_VERSION } from '../constants/terms';

interface FieldErrors {
  bankCode?: string;
  bankBranchCode?: string;
  accountNo?: string;
  accountName?: string;
}

const BankAccountPage: React.FC = () => {
  const { userId } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  // 口座は保存できたが一部店舗で即払い連携に失敗したときの注意書き（店舗側設定の不備）
  const [warningMessages, setWarningMessages] = useState<string[]>([]);
  const [bankAccountInfo, setBankAccountInfo] = useState<BankAccountResponse | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // フォーム
  const [bankCode, setBankCode] = useState('');
  const [bankBranchCode, setBankBranchCode] = useState('');
  const [depositType, setDepositType] = useState<DepositType>('1');
  const [accountNo, setAccountNo] = useState('');
  const [accountName, setAccountName] = useState('');
  const [isComposing, setIsComposing] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  // 即給非利用（管理画面登録）ワーカーは口座登録＝即給利用者登録の対象外。
  // null = 判定未確定。false 初期値だと getUser 解決前に登録フォームが表示され、
  // opt-out ワーカーが入力・送信までできてしまうため、確定までローディングに含める。
  const [sokyuOptOut, setSokyuOptOut] = useState<boolean | null>(null);
  useEffect(() => {
    if (!userId) return;
    apiService
      .getUser(userId)
      .then((res) => {
        setSokyuOptOut(res?.success && res.data ? Boolean(res.data.sokyu_opt_out) : false);
      })
      .catch(() => setSokyuOptOut(false));
  }, [userId]);

  // 課題88: 口座登録（＝即給申込）前に現行バージョンの即給利用規約への同意を必須とする。
  // Re:crew 利用規約・プライバシーポリシーは新規ユーザー登録時に同意済みのため、この画面では
  // 即給規約への同意のみを判定・取得する。null = 判定未確定。即給利用ワーカー
  // （sokyu_opt_out=false）のみ判定する。
  const [termsAgreed, setTermsAgreed] = useState<boolean | null>(null);
  const [consentSaving, setConsentSaving] = useState(false);
  const [consentError, setConsentError] = useState('');
  useEffect(() => {
    if (!userId) return;
    if (sokyuOptOut !== false) return; // opt-out / 未確定は同意判定不要
    apiService
      .getTermsConsentStatus(userId)
      .then((res) => {
        setTermsAgreed(res?.success && res.data ? Boolean(res.data.agreed) : false);
      })
      .catch(() => setTermsAgreed(false));
  }, [userId, sokyuOptOut]);

  const handleConsentAgree = async () => {
    if (!userId) return;
    try {
      setConsentSaving(true);
      setConsentError('');
      const res = await apiService.recordTermsConsent(userId, {
        tos_type: 'sokyu',
        tos_version: SOKYU_TERMS_VERSION,
        source: 'bank_registration',
      });
      if (res.success) {
        setTermsAgreed(true);
      } else {
        setConsentError(res.error || '同意の記録に失敗しました');
      }
    } catch (err) {
      setConsentError('同意の記録中にエラーが発生しました');
    } finally {
      setConsentSaving(false);
    }
  };

  // 同意しない場合は口座登録に進めないため前画面へ戻す
  const handleConsentClose = () => {
    navigate(-1);
  };

  const fetchBankAccount = useCallback(async () => {
    if (!userId) return;
    try {
      setLoading(true);
      const response = await apiService.getBankAccount(userId);
      if (response.success && response.data) {
        setBankAccountInfo(response.data);
        if (response.data.bank_account) {
          const ba = response.data.bank_account;
          setBankCode(ba.bank_code || '');
          setBankBranchCode(ba.bank_branch_code || '');
          setDepositType((ba.deposit_type as DepositType) || '1');
          setAccountNo(ba.account_no || '');
          setAccountName(ba.account_name || '');
        }
      }
    } catch (err: any) {
      setError('口座情報の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchBankAccount();
  }, [fetchBankAccount]);

  const validate = (): boolean => {
    const errors: FieldErrors = {};
    if (!/^\d{4}$/.test(bankCode)) errors.bankCode = '銀行コードは4桁の数字で入力してください';
    if (!/^\d{3}$/.test(bankBranchCode)) errors.bankBranchCode = '支店コードは3桁の数字で入力してください';
    if (!/^\d{7}$/.test(accountNo)) errors.accountNo = '口座番号は7桁の数字で入力してください';
    if (!accountName.trim()) {
      errors.accountName = '口座名義を入力してください';
    } else {
      const converted = convertToHankaku(accountName);
      if (converted.length > 30) {
        errors.accountName = '口座名義は30文字以内で入力してください';
      } else {
        const invalid = findInvalidChars(converted);
        if (invalid.length > 0) {
          errors.accountName = `口座名義に使用できない文字が含まれています: ${invalid.join('')}`;
        }
      }
    }

    setFieldErrors(errors);
    const errorKeys = Object.keys(errors) as (keyof FieldErrors)[];
    if (errorKeys.length > 0) {
      const fieldIdMap: Record<keyof FieldErrors, string> = {
        bankCode: 'bankCode',
        bankBranchCode: 'bankBranchCode',
        accountNo: 'accountNo',
        accountName: 'accountName',
      };
      scrollToFirstError(errorKeys.map(k => fieldIdMap[k]));
      return false;
    }
    return true;
  };

  // バリデーションのみ実行して確認ダイアログを開く
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    if (!validate()) {
      return;
    }

    if (!userId) {
      setError('ユーザー情報が取得できません');
      return;
    }

    setConfirmOpen(true);
  };

  // 確認ダイアログで「登録する」を押したときのAPI呼び出し
  const handleRegister = async () => {
    if (!userId) return;

    const bankAccount: BankAccount = {
      bank_code: bankCode,
      bank_branch_code: bankBranchCode,
      deposit_type: depositType,
      account_no: accountNo,
      account_name: accountName.trim(),
    };

    try {
      setSaving(true);
      setConfirmOpen(false);
      setWarningMessages([]);
      const response = await apiService.registerBankAccount(userId, bankAccount);

      if (response.success && response.data) {
        // 口座は保存できたが一部店舗で即払い連携に失敗した場合は、そのまま戻らず注意書きを見せる。
        // ワーカー側の操作では解消しないため、店舗へ連絡してもらう必要がある。
        const warnings = response.data.warnings ?? [];
        if (warnings.length > 0) {
          setFieldErrors({});
          setError('');
          setWarningMessages(warnings);
          setSuccessMessage('口座情報を保存しました。');
          return;
        }
        navigate(-1);
      } else {
        handleRegisterError(response.error || '口座情報の登録に失敗しました');
      }
    } catch (err: any) {
      handleRegisterError(err.message || '口座情報の登録中にエラーが発生しました');
    } finally {
      setSaving(false);
    }
  };

  // 即給からのエラーメッセージを解析し、項目別エラー（赤枠＋メッセージ）へ振り分ける。
  // 振り分けに失敗した場合のみ、上部の Alert に元のメッセージを表示する。
  const handleRegisterError = (rawMessage: string) => {
    const parsed = parseSokyuBankAccountError(rawMessage);
    if (parsed) {
      setFieldErrors(prev => ({ ...prev, ...parsed.fieldErrors }));
      const errorKeys = Object.keys(parsed.fieldErrors);
      if (errorKeys.length === 0 && parsed.storeConfigMessage) {
        // 店舗側設定だけが原因のケース。口座欄は正しいので「入力内容に誤り」とは案内しない。
        setError(parsed.storeConfigMessage);
        return;
      }
      // パースできた項目があれば、上部メッセージは簡潔にして詳細は各項目へ
      const suffix = [parsed.remainingMessage, parsed.storeConfigMessage]
        .filter(Boolean)
        .join(' / ');
      setError(
        suffix
          ? `入力内容に誤りがあります。各項目の表示をご確認ください。（${suffix}）`
          : '入力内容に誤りがあります。各項目の表示をご確認ください。'
      );
      if (errorKeys.length > 0) {
        scrollToFirstError(errorKeys);
      }
    } else {
      setError(rawMessage);
    }
  };

  if (
    loading ||
    (userId && sokyuOptOut === null) ||
    (sokyuOptOut === false && termsAgreed === null)
  ) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // 即給非利用ワーカーが直接URLで開いた場合のガード
  if (sokyuOptOut) {
    return (
      <Box sx={{ maxWidth: 480, mx: 'auto', p: 2 }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          即払いの対象外のため、口座登録は不要です。報酬は給与日に通常の銀行振込でお支払いされます。
        </Alert>
        <Button variant="outlined" onClick={() => navigate(-1)}>
          戻る
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 480, mx: 'auto', p: 2 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <AccountBalanceIcon /> 口座情報登録
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {successMessage && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage('')}>{successMessage}</Alert>}
      {warningMessages.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setWarningMessages([])}>
          {warningMessages.map((message, index) => (
            <Typography key={index} variant="body2">{message}</Typography>
          ))}
        </Alert>
      )}

      {/* 口座情報フォーム */}
      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>
            振込先の銀行口座情報を登録してください。
          </Typography>

          <form onSubmit={handleSubmit}>
            <TextField
              id="bankCode"
              label="銀行コード（4桁）"
              value={bankCode}
              onChange={(e) => {
                setBankCode(e.target.value.replace(/\D/g, '').slice(0, 4));
                if (fieldErrors.bankCode) setFieldErrors(prev => ({ ...prev, bankCode: undefined }));
              }}
              fullWidth
              margin="normal"
              required
              error={!!fieldErrors.bankCode}
              helperText={fieldErrors.bankCode || '銀行の4桁コードを入力'}
              inputProps={{ maxLength: 4, inputMode: 'numeric' }}
              placeholder="0001"
            />

            <TextField
              id="bankBranchCode"
              label="支店コード（3桁）"
              value={bankBranchCode}
              onChange={(e) => {
                setBankBranchCode(e.target.value.replace(/\D/g, '').slice(0, 3));
                if (fieldErrors.bankBranchCode) setFieldErrors(prev => ({ ...prev, bankBranchCode: undefined }));
              }}
              fullWidth
              margin="normal"
              required
              error={!!fieldErrors.bankBranchCode}
              helperText={fieldErrors.bankBranchCode || '支店の3桁コードを入力'}
              inputProps={{ maxLength: 3, inputMode: 'numeric' }}
              placeholder="001"
            />

            <TextField
              select
              label="預金種別"
              value={depositType}
              onChange={(e) => setDepositType(e.target.value as DepositType)}
              fullWidth
              margin="normal"
              required
            >
              <MenuItem value="1">普通</MenuItem>
              <MenuItem value="2">当座</MenuItem>
            </TextField>

            <TextField
              id="accountNo"
              label="口座番号（7桁）"
              value={accountNo}
              onChange={(e) => {
                setAccountNo(e.target.value.replace(/\D/g, '').slice(0, 7));
                if (fieldErrors.accountNo) setFieldErrors(prev => ({ ...prev, accountNo: undefined }));
              }}
              fullWidth
              margin="normal"
              required
              error={!!fieldErrors.accountNo}
              helperText={fieldErrors.accountNo || '口座番号の7桁を入力'}
              inputProps={{ maxLength: 7, inputMode: 'numeric' }}
              placeholder="1234567"
            />

            <TextField
              id="accountName"
              label="口座名義（半角カナ）"
              value={accountName}
              onChange={(e) => {
                const val = isComposing ? e.target.value : convertToHankaku(e.target.value);
                setAccountName(val);
                if (fieldErrors.accountName) setFieldErrors(prev => ({ ...prev, accountName: undefined }));
              }}
              onCompositionStart={() => setIsComposing(true)}
              onCompositionEnd={(e) => {
                setIsComposing(false);
                const val = convertToHankaku((e.target as HTMLInputElement).value);
                setAccountName(val);
              }}
              fullWidth
              margin="normal"
              required
              error={!!fieldErrors.accountName}
              helperText={fieldErrors.accountName || '半角カナ・英数字・記号で入力（全角は自動変換、30文字以内）'}
              inputProps={{ maxLength: 30 }}
              placeholder="ﾔﾏﾀﾞ ﾀﾛｳ"
            />

            <Box sx={{ mt: 3, display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                onClick={() => navigate(-1)}
                sx={{ flex: 1 }}
              >
                戻る
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={saving || !termsAgreed}
                sx={{ flex: 2 }}
              >
                {saving ? <CircularProgress size={24} /> : '確認する'}
              </Button>
            </Box>
          </form>
        </CardContent>
      </Card>

      {/* 登録内容確認ダイアログ */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>登録内容の確認</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            以下の内容で登録します。内容に誤りがないか確認してください。
          </Typography>
          <Divider sx={{ mb: 1.5 }} />
          {[
            { label: '銀行コード', value: bankCode },
            { label: '支店コード', value: bankBranchCode },
            { label: '預金種別', value: depositType === '1' ? '普通' : '当座' },
            { label: '口座番号', value: accountNo },
            { label: '口座名義', value: accountName.trim() },
          ].map(({ label, value }) => (
            <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75 }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>{label}</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{value}</Typography>
            </Box>
          ))}
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => setConfirmOpen(false)}
            disabled={saving}
            sx={{ flex: 1 }}
          >
            修正する
          </Button>
          <Button
            variant="contained"
            onClick={handleRegister}
            disabled={saving}
            sx={{ flex: 2 }}
          >
            {saving ? <CircularProgress size={20} /> : '登録する'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 課題88: 口座登録時の即給利用規約 同意ポップアップ（即給利用ワーカーかつ未同意時のみ表示） */}
      <TermsConsentPopup
        open={sokyuOptOut === false && termsAgreed === false}
        onAgree={handleConsentAgree}
        onClose={handleConsentClose}
        loading={consentSaving}
        errorMessage={consentError}
      />
    </Box>
  );
};

export default BankAccountPage;
